/**
 * 3D Infinite Grid Menu - WebGL2 Product Showcase
 * Neo-Brutalist Interactive Product Display
 */

// WebGL Shaders
const discVertShaderSource = `#version 300 es

uniform mat4 uWorldMatrix;
uniform mat4 uViewMatrix;
uniform mat4 uProjectionMatrix;
uniform vec3 uCameraPosition;
uniform vec4 uRotationAxisVelocity;

in vec3 aModelPosition;
in vec3 aModelNormal;
in vec2 aModelUvs;
in mat4 aInstanceMatrix;

out vec2 vUvs;
out float vAlpha;
flat out int vInstanceId;

#define PI 3.141593

void main() {
    vec4 worldPosition = uWorldMatrix * aInstanceMatrix * vec4(aModelPosition, 1.);

    vec3 centerPos = (uWorldMatrix * aInstanceMatrix * vec4(0., 0., 0., 1.)).xyz;
    float radius = length(centerPos.xyz);

    if (gl_VertexID > 0) {
        vec3 rotationAxis = uRotationAxisVelocity.xyz;
        float rotationVelocity = min(.15, uRotationAxisVelocity.w * 15.);
        vec3 stretchDir = normalize(cross(centerPos, rotationAxis));
        vec3 relativeVertexPos = normalize(worldPosition.xyz - centerPos);
        float strength = dot(stretchDir, relativeVertexPos);
        float invAbsStrength = min(0., abs(strength) - 1.);
        strength = rotationVelocity * sign(strength) * abs(invAbsStrength * invAbsStrength * invAbsStrength + 1.);
        worldPosition.xyz += stretchDir * strength;
    }

    worldPosition.xyz = radius * normalize(worldPosition.xyz);

    gl_Position = uProjectionMatrix * uViewMatrix * worldPosition;

    vAlpha = smoothstep(0.5, 1., normalize(worldPosition.xyz).z) * .9 + .1;
    vUvs = aModelUvs;
    vInstanceId = gl_InstanceID;
}
`;

const discFragShaderSource = `#version 300 es
precision highp float;

uniform sampler2D uTex;
uniform int uItemCount;
uniform int uAtlasSize;

out vec4 outColor;

in vec2 vUvs;
in float vAlpha;
flat in int vInstanceId;

void main() {
    int itemIndex = vInstanceId % uItemCount;
    int cellsPerRow = uAtlasSize;
    int cellX = itemIndex % cellsPerRow;
    int cellY = itemIndex / cellsPerRow;
    vec2 cellSize = vec2(1.0) / vec2(float(cellsPerRow));
    vec2 cellOffset = vec2(float(cellX), float(cellY)) * cellSize;

    ivec2 texSize = textureSize(uTex, 0);
    float imageAspect = float(texSize.x) / float(texSize.y);
    float containerAspect = 1.0;
    
    float scale = max(imageAspect / containerAspect, 
                     containerAspect / imageAspect);
    
    vec2 st = vec2(vUvs.x, 1.0 - vUvs.y);
    st = (st - 0.5) * scale + 0.5;
    
    st = clamp(st, 0.0, 1.0);
    st = st * cellSize + cellOffset;
    
    outColor = texture(uTex, st);
    outColor.a *= vAlpha;
}
`;

// Geometry Classes
class Face {
  constructor(a, b, c) {
    this.a = a;
    this.b = b;
    this.c = c;
  }
}

class Vertex {
  constructor(x, y, z) {
    this.position = [x, y, z];
    this.normal = [0, 0, 0];
    this.uv = [0, 0];
  }
}

class Geometry {
  constructor() {
    this.vertices = [];
    this.faces = [];
  }

  addVertex(...args) {
    for (let i = 0; i < args.length; i += 3) {
      this.vertices.push(new Vertex(args[i], args[i + 1], args[i + 2]));
    }
    return this;
  }

  addFace(...args) {
    for (let i = 0; i < args.length; i += 3) {
      this.faces.push(new Face(args[i], args[i + 1], args[i + 2]));
    }
    return this;
  }

  get lastVertex() {
    return this.vertices[this.vertices.length - 1];
  }

  subdivide(divisions = 1) {
    const midPointCache = {};
    let f = this.faces;

    for (let div = 0; div < divisions; ++div) {
      const newFaces = new Array(f.length * 4);

      f.forEach((face, ndx) => {
        const mAB = this.getMidPoint(face.a, face.b, midPointCache);
        const mBC = this.getMidPoint(face.b, face.c, midPointCache);
        const mCA = this.getMidPoint(face.c, face.a, midPointCache);

        const i = ndx * 4;
        newFaces[i + 0] = new Face(face.a, mAB, mCA);
        newFaces[i + 1] = new Face(face.b, mBC, mAB);
        newFaces[i + 2] = new Face(face.c, mCA, mBC);
        newFaces[i + 3] = new Face(mAB, mBC, mCA);
      });

      f = newFaces;
    }

    this.faces = f;
    return this;
  }

  spherize(radius = 1) {
    this.vertices.forEach(vertex => {
      const len = Math.sqrt(vertex.position[0] ** 2 + vertex.position[1] ** 2 + vertex.position[2] ** 2);
      vertex.normal[0] = vertex.position[0] / len;
      vertex.normal[1] = vertex.position[1] / len;
      vertex.normal[2] = vertex.position[2] / len;
      vertex.position[0] = vertex.normal[0] * radius;
      vertex.position[1] = vertex.normal[1] * radius;
      vertex.position[2] = vertex.normal[2] * radius;
    });
    return this;
  }

  get data() {
    return {
      vertices: new Float32Array(this.vertices.flatMap(v => v.position)),
      indices: new Uint16Array(this.faces.flatMap(f => [f.a, f.b, f.c])),
      normals: new Float32Array(this.vertices.flatMap(v => v.normal)),
      uvs: new Float32Array(this.vertices.flatMap(v => v.uv))
    };
  }

  getMidPoint(ndxA, ndxB, cache) {
    const cacheKey = ndxA < ndxB ? `k_${ndxB}_${ndxA}` : `k_${ndxA}_${ndxB}`;
    if (cache.hasOwnProperty(cacheKey)) {
      return cache[cacheKey];
    }
    const a = this.vertices[ndxA].position;
    const b = this.vertices[ndxB].position;
    const ndx = this.vertices.length;
    cache[cacheKey] = ndx;
    this.addVertex((a[0] + b[0]) * 0.5, (a[1] + b[1]) * 0.5, (a[2] + b[2]) * 0.5);
    return ndx;
  }
}

class IcosahedronGeometry extends Geometry {
  constructor() {
    super();
    const t = Math.sqrt(5) * 0.5 + 0.5;
    this.addVertex(
      -1, t, 0, 1, t, 0, -1, -t, 0, 1, -t, 0,
      0, -1, t, 0, 1, t, 0, -1, -t, 0, 1, -t,
      t, 0, -1, t, 0, 1, -t, 0, -1, -t, 0, 1
    ).addFace(
      0, 11, 5, 0, 5, 1, 0, 1, 7, 0, 7, 10, 0, 10, 11,
      1, 5, 9, 5, 11, 4, 11, 10, 2, 10, 7, 6, 7, 1, 8,
      3, 9, 4, 3, 4, 2, 3, 2, 6, 3, 6, 8, 3, 8, 9,
      4, 9, 5, 2, 4, 11, 6, 2, 10, 8, 6, 7, 9, 8, 1
    );
  }
}

class DiscGeometry extends Geometry {
  constructor(steps = 4, radius = 1) {
    super();
    const safeSteps = Math.max(4, steps);
    const alpha = (2 * Math.PI) / safeSteps;

    this.addVertex(0, 0, 0);
    this.lastVertex.uv[0] = 0.5;
    this.lastVertex.uv[1] = 0.5;

    for (let i = 0; i < safeSteps; ++i) {
      const x = Math.cos(alpha * i);
      const y = Math.sin(alpha * i);
      this.addVertex(radius * x, radius * y, 0);
      this.lastVertex.uv[0] = x * 0.5 + 0.5;
      this.lastVertex.uv[1] = y * 0.5 + 0.5;

      if (i > 0) {
        this.addFace(0, i, i + 1);
      }
    }
    this.addFace(0, safeSteps, 1);
  }
}

class RectangleGeometry extends Geometry {
  constructor(width = 1.2, height = 1.6) {
    super();
    
    const halfWidth = width * 0.5;
    const halfHeight = height * 0.5;
    
    // Create rectangle vertices (4 corners)
    // Bottom-left
    this.addVertex(-halfWidth, -halfHeight, 0);
    this.lastVertex.uv[0] = 0;
    this.lastVertex.uv[1] = 0;
    
    // Bottom-right  
    this.addVertex(halfWidth, -halfHeight, 0);
    this.lastVertex.uv[0] = 1;
    this.lastVertex.uv[1] = 0;
    
    // Top-right
    this.addVertex(halfWidth, halfHeight, 0);
    this.lastVertex.uv[0] = 1;
    this.lastVertex.uv[1] = 1;
    
    // Top-left
    this.addVertex(-halfWidth, halfHeight, 0);
    this.lastVertex.uv[0] = 0;
    this.lastVertex.uv[1] = 1;
    
    // Create two triangular faces to form rectangle
    // First triangle: bottom-left, bottom-right, top-right
    this.addFace(0, 1, 2);
    
    // Second triangle: bottom-left, top-right, top-left  
    this.addFace(0, 2, 3);
  }
}

// WebGL Utilities
function createShader(gl, type, source) {
  const shader = gl.createShader(type);
  if (!shader) return null;
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  
  if (gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    return shader;
  }

  gl.deleteShader(shader);
  return null;
}

function createProgram(gl, shaderSources, transformFeedbackVaryings, attribLocations) {
  const program = gl.createProgram();
  if (!program) return null;

  [gl.VERTEX_SHADER, gl.FRAGMENT_SHADER].forEach((type, ndx) => {
    const shader = createShader(gl, type, shaderSources[ndx]);
    if (shader) {
      gl.attachShader(program, shader);
    }
  });

  if (transformFeedbackVaryings) {
    gl.transformFeedbackVaryings(program, transformFeedbackVaryings, gl.SEPARATE_ATTRIBS);
  }

  if (attribLocations) {
    for (const attrib in attribLocations) {
      if (attribLocations.hasOwnProperty(attrib)) {
        gl.bindAttribLocation(program, attribLocations[attrib], attrib);
      }
    }
  }

  gl.linkProgram(program);
  
  if (gl.getProgramParameter(program, gl.LINK_STATUS)) {
    return program;
  }

  gl.deleteProgram(program);
  return null;
}

function makeVertexArray(gl, bufLocNumElmPairs, indices) {
  const va = gl.createVertexArray();
  if (!va) return null;

  gl.bindVertexArray(va);

  for (const [buffer, loc, numElem] of bufLocNumElmPairs) {
    if (loc === -1) continue;
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, numElem, gl.FLOAT, false, 0, 0);
  }

  if (indices) {
    const indexBuffer = gl.createBuffer();
    if (indexBuffer) {
      gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, indexBuffer);
      gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, indices, gl.STATIC_DRAW);
    }
  }

  gl.bindVertexArray(null);
  return va;
}

function resizeCanvasToDisplaySize(canvas) {
  const dpr = Math.min(2, window.devicePixelRatio || 1);
  const displayWidth = Math.round(canvas.clientWidth * dpr);
  const displayHeight = Math.round(canvas.clientHeight * dpr);
  const needResize = canvas.width !== displayWidth || canvas.height !== displayHeight;
  if (needResize) {
    canvas.width = displayWidth;
    canvas.height = displayHeight;
  }
  return needResize;
}

function makeBuffer(gl, sizeOrData, usage) {
  const buf = gl.createBuffer();
  if (!buf) {
    throw new Error('Failed to create WebGL buffer.');
  }
  gl.bindBuffer(gl.ARRAY_BUFFER, buf);

  if (typeof sizeOrData === 'number') {
    gl.bufferData(gl.ARRAY_BUFFER, sizeOrData, usage);
  } else {
    gl.bufferData(gl.ARRAY_BUFFER, sizeOrData, usage);
  }

  gl.bindBuffer(gl.ARRAY_BUFFER, null);
  return buf;
}

function createAndSetupTexture(gl, minFilter, magFilter, wrapS, wrapT) {
  const texture = gl.createTexture();
  if (!texture) {
    throw new Error('Failed to create WebGL texture.');
  }
  gl.bindTexture(gl.TEXTURE_2D, texture);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, wrapS);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, wrapT);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, minFilter);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, magFilter);
  return texture;
}

// Math utilities (simplified gl-matrix functions)
const mat4 = {
  create: () => new Float32Array(16),
  identity: (out) => {
    out[0] = 1; out[1] = 0; out[2] = 0; out[3] = 0;
    out[4] = 0; out[5] = 1; out[6] = 0; out[7] = 0;
    out[8] = 0; out[9] = 0; out[10] = 1; out[11] = 0;
    out[12] = 0; out[13] = 0; out[14] = 0; out[15] = 1;
    return out;
  },
  perspective: (out, fovy, aspect, near, far) => {
    const f = 1.0 / Math.tan(fovy / 2);
    const nf = 1 / (near - far);
    out[0] = f / aspect; out[1] = 0; out[2] = 0; out[3] = 0;
    out[4] = 0; out[5] = f; out[6] = 0; out[7] = 0;
    out[8] = 0; out[9] = 0; out[10] = (far + near) * nf; out[11] = -1;
    out[12] = 0; out[13] = 0; out[14] = 2 * far * near * nf; out[15] = 0;
    return out;
  },
  lookAt: (out, eye, center, up) => {
    const x0 = eye[0], x1 = eye[1], x2 = eye[2];
    const y0 = center[0], y1 = center[1], y2 = center[2];
    const up0 = up[0], up1 = up[1], up2 = up[2];
    
    let len = x0 - y0;
    const len2 = x1 - y1;
    const len3 = x2 - y2;
    len = Math.sqrt(len * len + len2 * len2 + len3 * len3);
    
    if (!len) {
      out[14] = -x2;
    } else {
      len = 1 / len;
      out[10] = (x0 - y0) * len;
      out[6] = len2 * len;
      out[14] = len3 * len;
    }
    
    return out;
  },
  multiply: (out, a, b) => {
    const a00 = a[0], a01 = a[1], a02 = a[2], a03 = a[3];
    const a10 = a[4], a11 = a[5], a12 = a[6], a13 = a[7];
    const a20 = a[8], a21 = a[9], a22 = a[10], a23 = a[11];
    const a30 = a[12], a31 = a[13], a32 = a[14], a33 = a[15];

    let b0 = b[0], b1 = b[1], b2 = b[2], b3 = b[3];
    out[0] = b0*a00 + b1*a10 + b2*a20 + b3*a30;
    out[1] = b0*a01 + b1*a11 + b2*a21 + b3*a31;
    out[2] = b0*a02 + b1*a12 + b2*a22 + b3*a32;
    out[3] = b0*a03 + b1*a13 + b2*a23 + b3*a33;

    b0 = b[4]; b1 = b[5]; b2 = b[6]; b3 = b[7];
    out[4] = b0*a00 + b1*a10 + b2*a20 + b3*a30;
    out[5] = b0*a01 + b1*a11 + b2*a21 + b3*a31;
    out[6] = b0*a02 + b1*a12 + b2*a22 + b3*a32;
    out[7] = b0*a03 + b1*a13 + b2*a23 + b3*a33;

    b0 = b[8]; b1 = b[9]; b2 = b[10]; b3 = b[11];
    out[8] = b0*a00 + b1*a10 + b2*a20 + b3*a30;
    out[9] = b0*a01 + b1*a11 + b2*a21 + b3*a31;
    out[10] = b0*a02 + b1*a12 + b2*a22 + b3*a32;
    out[11] = b0*a03 + b1*a13 + b2*a23 + b3*a33;

    b0 = b[12]; b1 = b[13]; b2 = b[14]; b3 = b[15];
    out[12] = b0*a00 + b1*a10 + b2*a20 + b3*a30;
    out[13] = b0*a01 + b1*a11 + b2*a21 + b3*a31;
    out[14] = b0*a02 + b1*a12 + b2*a22 + b3*a32;
    out[15] = b0*a03 + b1*a13 + b2*a23 + b3*a33;
    return out;
  },
  invert: (out, a) => {
    const a00 = a[0], a01 = a[1], a02 = a[2], a03 = a[3];
    const a10 = a[4], a11 = a[5], a12 = a[6], a13 = a[7];
    const a20 = a[8], a21 = a[9], a22 = a[10], a23 = a[11];
    const a30 = a[12], a31 = a[13], a32 = a[14], a33 = a[15];

    const b00 = a00 * a11 - a01 * a10;
    const b01 = a00 * a12 - a02 * a10;
    const b02 = a00 * a13 - a03 * a10;
    const b03 = a01 * a12 - a02 * a11;
    const b04 = a01 * a13 - a03 * a11;
    const b05 = a02 * a13 - a03 * a12;
    const b06 = a20 * a31 - a21 * a30;
    const b07 = a20 * a32 - a22 * a30;
    const b08 = a20 * a33 - a23 * a30;
    const b09 = a21 * a32 - a22 * a31;
    const b10 = a21 * a33 - a23 * a31;
    const b11 = a22 * a33 - a23 * a32;

    let det = b00 * b11 - b01 * b10 + b02 * b09 + b03 * b08 - b04 * b07 + b05 * b06;

    if (!det) {
      return null;
    }
    det = 1.0 / det;

    out[0] = (a11 * b11 - a12 * b10 + a13 * b09) * det;
    out[1] = (a02 * b10 - a01 * b11 - a03 * b09) * det;
    out[2] = (a31 * b05 - a32 * b04 + a33 * b03) * det;
    out[3] = (a22 * b04 - a21 * b05 - a23 * b03) * det;
    out[4] = (a12 * b08 - a10 * b11 - a13 * b07) * det;
    out[5] = (a00 * b11 - a02 * b08 + a03 * b07) * det;
    out[6] = (a32 * b02 - a30 * b05 - a33 * b01) * det;
    out[7] = (a20 * b05 - a22 * b02 + a23 * b01) * det;
    out[8] = (a10 * b10 - a11 * b08 + a13 * b06) * det;
    out[9] = (a01 * b08 - a00 * b10 - a03 * b06) * det;
    out[10] = (a30 * b04 - a31 * b02 + a33 * b00) * det;
    out[11] = (a21 * b02 - a20 * b04 - a23 * b00) * det;
    out[12] = (a11 * b07 - a10 * b09 - a12 * b06) * det;
    out[13] = (a00 * b09 - a01 * b07 + a02 * b06) * det;
    out[14] = (a31 * b01 - a30 * b03 - a32 * b00) * det;
    out[15] = (a20 * b03 - a21 * b01 + a22 * b00) * det;

    return out;
  }
};

const quat = {
  create: () => new Float32Array([0, 0, 0, 1]),
  multiply: (out, a, b) => {
    const ax = a[0], ay = a[1], az = a[2], aw = a[3];
    const bx = b[0], by = b[1], bz = b[2], bw = b[3];

    out[0] = ax * bw + aw * bx + ay * bz - az * by;
    out[1] = ay * bw + aw * by + az * bx - ax * bz;
    out[2] = az * bw + aw * bz + ax * by - ay * bx;
    out[3] = aw * bw - ax * bx - ay * by - az * bz;
    return out;
  },
  setAxisAngle: (out, axis, rad) => {
    const halfAngle = rad * 0.5;
    const s = Math.sin(halfAngle);
    out[0] = s * axis[0];
    out[1] = s * axis[1];
    out[2] = s * axis[2];
    out[3] = Math.cos(halfAngle);
    return out;
  },
  normalize: (out, a) => {
    let x = a[0], y = a[1], z = a[2], w = a[3];
    let len = x*x + y*y + z*z + w*w;
    if (len > 0) {
      len = 1 / Math.sqrt(len);
      out[0] = a[0] * len;
      out[1] = a[1] * len;
      out[2] = a[2] * len;
      out[3] = a[3] * len;
    }
    return out;
  },
  slerp: (out, a, b, t) => {
    const ax = a[0], ay = a[1], az = a[2], aw = a[3];
    let bx = b[0], by = b[1], bz = b[2], bw = b[3];

    let omega, cosom, sinom, scale0, scale1;

    cosom = ax * bx + ay * by + az * bz + aw * bw;
    if (cosom < 0.0) {
      cosom = -cosom;
      bx = - bx;
      by = - by;
      bz = - bz;
      bw = - bw;
    }
    if ((1.0 - cosom) > 0.000001) {
      omega = Math.acos(cosom);
      sinom = Math.sin(omega);
      scale0 = Math.sin((1.0 - t) * omega) / sinom;
      scale1 = Math.sin(t * omega) / sinom;
    } else {
      scale0 = 1.0 - t;
      scale1 = t;
    }
    out[0] = scale0 * ax + scale1 * bx;
    out[1] = scale0 * ay + scale1 * by;
    out[2] = scale0 * az + scale1 * bz;
    out[3] = scale0 * aw + scale1 * bw;

    return out;
  }
};

const vec3 = {
  create: () => new Float32Array(3),
  fromValues: (x, y, z) => new Float32Array([x, y, z]),
  normalize: (out, a) => {
    let x = a[0], y = a[1], z = a[2];
    let len = x*x + y*y + z*z;
    if (len > 0) {
      len = 1 / Math.sqrt(len);
      out[0] = a[0] * len;
      out[1] = a[1] * len;
      out[2] = a[2] * len;
    }
    return out;
  },
  cross: (out, a, b) => {
    const ax = a[0], ay = a[1], az = a[2];
    const bx = b[0], by = b[1], bz = b[2];

    out[0] = ay * bz - az * by;
    out[1] = az * bx - ax * bz;
    out[2] = ax * by - ay * bx;
    return out;
  },
  dot: (a, b) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2],
  transformQuat: (out, a, q) => {
    const x = a[0], y = a[1], z = a[2];
    const qx = q[0], qy = q[1], qz = q[2], qw = q[3];

    const ix = qw * x + qy * z - qz * y;
    const iy = qw * y + qz * x - qx * z;
    const iz = qw * z + qx * y - qy * x;
    const iw = -qx * x - qy * y - qz * z;

    out[0] = ix * qw + iw * -qx + iy * -qz - iz * -qy;
    out[1] = iy * qw + iw * -qy + iz * -qx - ix * -qz;
    out[2] = iz * qw + iw * -qz + ix * -qy - iy * -qx;
    return out;
  }
};

const vec2 = {
  create: () => new Float32Array(2),
  set: (out, x, y) => {
    out[0] = x;
    out[1] = y;
    return out;
  },
  copy: (out, a) => {
    out[0] = a[0];
    out[1] = a[1];
    return out;
  },
  sub: (out, a, b) => {
    out[0] = a[0] - b[0];
    out[1] = a[1] - b[1];
    return out;
  },
  scale: (out, a, b) => {
    out[0] = a[0] * b;
    out[1] = a[1] * b;
    return out;
  },
  add: (out, a, b) => {
    out[0] = a[0] + b[0];
    out[1] = a[1] + b[1];
    return out;
  },
  sqrLen: (a) => a[0] * a[0] + a[1] * a[1]
};

// Arcball Control Class
class ArcballControl {
  constructor(canvas, updateCallback) {
    this.canvas = canvas;
    this.updateCallback = updateCallback || (() => {});

    this.isPointerDown = false;
    this.orientation = quat.create();
    this.pointerRotation = quat.create();
    this.rotationVelocity = 0;
    this.rotationAxis = vec3.fromValues(1, 0, 0);

    this.snapDirection = vec3.fromValues(0, 0, -1);
    this.snapTargetDirection = null;

    this.pointerPos = vec2.create();
    this.previousPointerPos = vec2.create();
    this._rotationVelocity = 0;
    this._combinedQuat = quat.create();

    this.EPSILON = 0.1;
    this.IDENTITY_QUAT = quat.create();

    this.bindEvents();
  }

  bindEvents() {
    this.canvas.addEventListener('pointerdown', (e) => {
      vec2.set(this.pointerPos, e.clientX, e.clientY);
      vec2.copy(this.previousPointerPos, this.pointerPos);
      this.isPointerDown = true;
    });

    this.canvas.addEventListener('pointerup', () => {
      this.isPointerDown = false;
    });

    this.canvas.addEventListener('pointerleave', () => {
      this.isPointerDown = false;
    });

    this.canvas.addEventListener('pointermove', (e) => {
      if (this.isPointerDown) {
        vec2.set(this.pointerPos, e.clientX, e.clientY);
      }
    });

    this.canvas.style.touchAction = 'none';
  }

  update(deltaTime, targetFrameDuration = 16) {
    const timeScale = deltaTime / targetFrameDuration + 0.00001;
    let angleFactor = timeScale;
    const snapRotation = quat.create();

    if (this.isPointerDown) {
      const INTENSITY = 0.3 * timeScale;
      const ANGLE_AMPLIFICATION = 5 / timeScale;
      const midPointerPos = vec2.sub(vec2.create(), this.pointerPos, this.previousPointerPos);
      vec2.scale(midPointerPos, midPointerPos, INTENSITY);

      if (vec2.sqrLen(midPointerPos) > this.EPSILON) {
        vec2.add(midPointerPos, this.previousPointerPos, midPointerPos);

        const p = this.project(midPointerPos);
        const q = this.project(this.previousPointerPos);
        const a = vec3.normalize(vec3.create(), p);
        const b = vec3.normalize(vec3.create(), q);

        vec2.copy(this.previousPointerPos, midPointerPos);

        angleFactor *= ANGLE_AMPLIFICATION;

        this.quatFromVectors(a, b, this.pointerRotation, angleFactor);
      } else {
        quat.slerp(this.pointerRotation, this.pointerRotation, this.IDENTITY_QUAT, INTENSITY);
      }
    } else {
      const INTENSITY = 0.1 * timeScale;
      quat.slerp(this.pointerRotation, this.pointerRotation, this.IDENTITY_QUAT, INTENSITY);

      if (this.snapTargetDirection) {
        const SNAPPING_INTENSITY = 0.2;
        const a = this.snapTargetDirection;
        const b = this.snapDirection;
        const sqrDist = vec3.squaredDistance(a, b);
        const distanceFactor = Math.max(0.1, 1 - sqrDist * 10);
        angleFactor *= SNAPPING_INTENSITY * distanceFactor;
        this.quatFromVectors(a, b, snapRotation, angleFactor);
      }
    }

    const combinedQuat = quat.multiply(quat.create(), snapRotation, this.pointerRotation);
    this.orientation = quat.multiply(quat.create(), combinedQuat, this.orientation);
    quat.normalize(this.orientation, this.orientation);

    const RA_INTENSITY = 0.8 * timeScale;
    quat.slerp(this._combinedQuat, this._combinedQuat, combinedQuat, RA_INTENSITY);
    quat.normalize(this._combinedQuat, this._combinedQuat);

    const rad = Math.acos(this._combinedQuat[3]) * 2.0;
    const s = Math.sin(rad / 2.0);
    let rv = 0;
    if (s > 0.000001) {
      rv = rad / (2 * Math.PI);
      this.rotationAxis[0] = this._combinedQuat[0] / s;
      this.rotationAxis[1] = this._combinedQuat[1] / s;
      this.rotationAxis[2] = this._combinedQuat[2] / s;
    }

    const RV_INTENSITY = 0.5 * timeScale;
    this._rotationVelocity += (rv - this._rotationVelocity) * RV_INTENSITY;
    this.rotationVelocity = this._rotationVelocity / timeScale;

    this.updateCallback(deltaTime);
  }

  quatFromVectors(a, b, out, angleFactor = 1) {
    const axis = vec3.cross(vec3.create(), a, b);
    vec3.normalize(axis, axis);
    const d = Math.max(-1, Math.min(1, vec3.dot(a, b)));
    const angle = Math.acos(d) * angleFactor;
    quat.setAxisAngle(out, axis, angle);
    return { q: out, axis, angle };
  }

  project(pos) {
    const r = 2;
    const w = this.canvas.clientWidth;
    const h = this.canvas.clientHeight;
    const s = Math.max(w, h) - 1;

    const x = (2 * pos[0] - w - 1) / s;
    const y = (2 * pos[1] - h - 1) / s;
    let z = 0;
    const xySq = x * x + y * y;
    const rSq = r * r;

    if (xySq <= rSq / 2.0) {
      z = Math.sqrt(rSq - xySq);
    } else {
      z = rSq / Math.sqrt(xySq);
    }
    return vec3.fromValues(-x, y, z);
  }
}

// Add missing vec3 functions
vec3.squaredDistance = (a, b) => {
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  const dz = b[2] - a[2];
  return dx*dx + dy*dy + dz*dz;
};

// Main InfiniteGridMenu Class
class InfiniteGridMenu {
  constructor(canvas, items, onActiveItemChange, onMovementChange, onInit) {
    this.canvas = canvas;
    this.items = items || [];
    this.onActiveItemChange = onActiveItemChange || (() => {});
    this.onMovementChange = onMovementChange || (() => {});

    this.gl = null;
    this.discProgram = null;
    this.discVAO = null;
    this.worldMatrix = mat4.create();
    this.tex = null;

    this.viewportSize = vec2.create();
    this.drawBufferSize = vec2.create();

    this.instancePositions = [];
    this.DISC_INSTANCE_COUNT = 0;
    this.atlasSize = 1;

    this._time = 0;
    this._deltaTime = 0;
    this._deltaFrames = 0;
    this._frames = 0;

    this.movementActive = false;
    this.currentActiveIndex = 0;
    this.clickEnabled = true;

    this.TARGET_FRAME_DURATION = 1000 / 60;
    this.SPHERE_RADIUS = 2;

    this.camera = {
      matrix: mat4.create(),
      near: 0.1,
      far: 40,
      fov: Math.PI / 4,
      aspect: 1,
      position: vec3.fromValues(0, 0, 3),
      up: vec3.fromValues(0, 1, 0),
      matrices: {
        view: mat4.create(),
        projection: mat4.create(),
        inversProjection: mat4.create()
      }
    };

    this.smoothRotationVelocity = 0;
    this.scaleFactor = 1.0;

    mat4.identity(this.worldMatrix);
    
    this.init(onInit);
  }

  resize() {
    const needsResize = resizeCanvasToDisplaySize(this.canvas);
    if (!this.gl) return;
    if (needsResize) {
      this.gl.viewport(0, 0, this.gl.drawingBufferWidth, this.gl.drawingBufferHeight);
    }
    this.updateProjectionMatrix();
  }

  run(time = 0) {
    this._deltaTime = Math.min(32, time - this._time);
    this._time = time;
    this._deltaFrames = this._deltaTime / this.TARGET_FRAME_DURATION;
    this._frames += this._deltaFrames;

    this.animate(this._deltaTime);
    this.render();

    requestAnimationFrame(t => this.run(t));
  }

  init(onInit) {
    const gl = this.canvas.getContext('webgl2', {
      antialias: true,
      alpha: false
    });
    if (!gl) {
      throw new Error('No WebGL 2 context!');
    }
    this.gl = gl;

    vec2.set(this.viewportSize, this.canvas.clientWidth, this.canvas.clientHeight);
    vec2.copy(this.drawBufferSize, this.viewportSize);

    this.discProgram = createProgram(gl, [discVertShaderSource, discFragShaderSource], null, {
      aModelPosition: 0,
      aModelNormal: 1,
      aModelUvs: 2,
      aInstanceMatrix: 3
    });

    this.discLocations = {
      aModelPosition: gl.getAttribLocation(this.discProgram, 'aModelPosition'),
      aModelUvs: gl.getAttribLocation(this.discProgram, 'aModelUvs'),
      aInstanceMatrix: gl.getAttribLocation(this.discProgram, 'aInstanceMatrix'),
      uWorldMatrix: gl.getUniformLocation(this.discProgram, 'uWorldMatrix'),
      uViewMatrix: gl.getUniformLocation(this.discProgram, 'uViewMatrix'),
      uProjectionMatrix: gl.getUniformLocation(this.discProgram, 'uProjectionMatrix'),
      uCameraPosition: gl.getUniformLocation(this.discProgram, 'uCameraPosition'),
      uRotationAxisVelocity: gl.getUniformLocation(this.discProgram, 'uRotationAxisVelocity'),
      uTex: gl.getUniformLocation(this.discProgram, 'uTex'),
      uItemCount: gl.getUniformLocation(this.discProgram, 'uItemCount'),
      uAtlasSize: gl.getUniformLocation(this.discProgram, 'uAtlasSize')
    };

    // Use rectangular geometry for product cards instead of circular discs
    this.cardGeo = new RectangleGeometry(1.2, 1.6);
    this.cardBuffers = this.cardGeo.data;
    this.discVAO = makeVertexArray(
      gl,
      [
        [makeBuffer(gl, this.cardBuffers.vertices, gl.STATIC_DRAW), this.discLocations.aModelPosition, 3],
        [makeBuffer(gl, this.cardBuffers.uvs, gl.STATIC_DRAW), this.discLocations.aModelUvs, 2]
      ],
      this.cardBuffers.indices
    );

    this.icoGeo = new IcosahedronGeometry();
    this.icoGeo.subdivide(1).spherize(this.SPHERE_RADIUS);
    this.instancePositions = this.icoGeo.vertices.map(v => v.position);
    this.DISC_INSTANCE_COUNT = this.icoGeo.vertices.length;
    this.initDiscInstances(this.DISC_INSTANCE_COUNT);
    this.initTexture();
    this.control = new ArcballControl(this.canvas, deltaTime => this.onControlUpdate(deltaTime));
    
    // Add click handlers for product interaction
    this.setupClickHandlers();

    this.updateCameraMatrix();
    this.updateProjectionMatrix();

    this.resize();

    if (onInit) {
      onInit(this);
    }
  }

  // Setup click handlers for 3D product cards
  setupClickHandlers() {
    if (!this.canvas) return;

    this.canvas.addEventListener('click', (e) => {
      // Only handle clicks if not dragging and click is enabled
      if (this.control.isPointerDown || !this.clickEnabled) return;
      
      // Debounce clicks to prevent accidental double-clicks
      if (this.clickTimeout) return;
      
      this.clickTimeout = setTimeout(() => {
        this.clickTimeout = null;
      }, 300);

      // Handle the click on current active item
      this.handleProductCardClick(this.currentActiveIndex);
    });

    // Handle keyboard interactions for accessibility
    this.canvas.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this.handleProductCardClick(this.currentActiveIndex);
      }
    });

    // Make canvas focusable for keyboard access
    this.canvas.setAttribute('tabindex', '0');
    this.canvas.setAttribute('role', 'button');
    this.canvas.setAttribute('aria-label', '3D Product Showcase - Click to interact');
  }

  // Handle product card click/interaction
  handleProductCardClick(itemIndex) {
    if (itemIndex < 0 || itemIndex >= this.items.length) return;
    
    const product = this.items[itemIndex];
    if (!product) return;

    // Find corresponding hidden HTML product card
    const hiddenProductCard = document.querySelector(
      `.hidden-product-card[data-product-index="${itemIndex}"]`
    );

    if (!hiddenProductCard) {
      // Fallback: navigate directly to product page
      if (product.link) {
        if (product.link.startsWith('http')) {
          window.open(product.link, '_blank');
        } else {
          window.location.href = product.link;
        }
      }
      return;
    }

    // Show click feedback on 3D canvas
    this.showClickFeedback();

    // Check for modifier keys to determine action
    const shouldAddToCart = !event.shiftKey && !event.ctrlKey && !event.metaKey;
    
    if (shouldAddToCart && product.available) {
      // Trigger add to cart for available products
      const addToCartForm = hiddenProductCard.querySelector('.neo-card__cart-form');
      const addToCartBtn = hiddenProductCard.querySelector('.hidden-add-to-cart');
      
      if (addToCartForm && addToCartBtn && !addToCartBtn.disabled) {
        // Trigger the existing neo product card system
        if (window.neoProductCards) {
          window.neoProductCards.handleAddToCart(addToCartForm);
        } else {
          // Fallback: submit form directly
          addToCartForm.dispatchEvent(new Event('submit', { cancelable: true }));
        }
      } else {
        // Navigate to product page if can't add to cart
        this.navigateToProduct(product, hiddenProductCard);
      }
    } else {
      // Navigate to product page
      this.navigateToProduct(product, hiddenProductCard);
    }
  }

  // Navigate to product page
  navigateToProduct(product, hiddenProductCard) {
    const productLink = hiddenProductCard.querySelector('.hidden-product-link');
    if (productLink) {
      // Trigger analytics if available
      if (window.neoProductCards) {
        window.neoProductCards.trackProductView(productLink);
      }
      
      // Navigate to product
      const href = productLink.getAttribute('href');
      if (href) {
        if (href.startsWith('http')) {
          window.open(href, '_blank');
        } else {
          window.location.href = href;
        }
      }
    } else if (product.link) {
      // Direct fallback
      if (product.link.startsWith('http')) {
        window.open(product.link, '_blank');
      } else {
        window.location.href = product.link;
      }
    }
  }

  // Visual feedback for clicks
  showClickFeedback() {
    // Temporarily disable clicks to prevent rapid firing
    this.clickEnabled = false;
    
    // Add visual pulse effect to canvas
    this.canvas.style.transform = 'scale(0.98)';
    this.canvas.style.transition = 'transform 0.1s ease';
    
    setTimeout(() => {
      this.canvas.style.transform = 'scale(1)';
      setTimeout(() => {
        this.canvas.style.transition = '';
        this.clickEnabled = true;
      }, 100);
    }, 50);
  }

  initTexture() {
    if (!this.gl) return;
    const gl = this.gl;
    this.tex = createAndSetupTexture(gl, gl.LINEAR, gl.LINEAR, gl.CLAMP_TO_EDGE, gl.CLAMP_TO_EDGE);

    const itemCount = Math.max(1, this.items.length);
    this.atlasSize = Math.ceil(Math.sqrt(itemCount));
    const cellSize = 512;
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    canvas.width = this.atlasSize * cellSize;
    canvas.height = this.atlasSize * cellSize;

    // Load product images
    Promise.all(
      this.items.map(
        item =>
          new Promise(resolve => {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = () => resolve({ img, item });
            img.onerror = () => {
              // Fallback to a default color
              const fallbackCanvas = document.createElement('canvas');
              fallbackCanvas.width = cellSize;
              fallbackCanvas.height = cellSize;
              const fallbackCtx = fallbackCanvas.getContext('2d');
              fallbackCtx.fillStyle = '#f0f0f0';
              fallbackCtx.fillRect(0, 0, cellSize, cellSize);
              resolve({ img: fallbackCanvas, item });
            };
            img.src = item.image;
          })
      )
    ).then(imageData => {
      imageData.forEach((data, i) => {
        const x = (i % this.atlasSize) * cellSize;
        const y = Math.floor(i / this.atlasSize) * cellSize;
        
        // Render 3D product card texture
        this.renderProductCardTexture(ctx, data.img, data.item, x, y, cellSize);
      });

      gl.bindTexture(gl.TEXTURE_2D, this.tex);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, canvas);
      gl.generateMipmap(gl.TEXTURE_2D);
    });
  }

  // Render product card as texture - Neo-Brutalist style
  renderProductCardTexture(ctx, img, item, x, y, size) {
    const padding = 20;
    const cardPadding = 16;
    const imageSize = size - (padding * 2);
    const cardHeight = size - padding;
    
    // Card background - Neo-Brutalist
    ctx.fillStyle = '#FFFEF7';
    ctx.fillRect(x + padding/2, y + padding/2, imageSize, cardHeight);
    
    // Bold border
    ctx.strokeStyle = '#2C3E50';
    ctx.lineWidth = 6;
    ctx.strokeRect(x + padding/2, y + padding/2, imageSize, cardHeight);
    
    // Shadow effect
    ctx.fillStyle = 'rgba(44, 62, 80, 0.4)';
    ctx.fillRect(x + padding/2 + 8, y + padding/2 + 8, imageSize, cardHeight);
    
    // Card background (over shadow)
    ctx.fillStyle = '#FFFEF7';
    ctx.fillRect(x + padding/2, y + padding/2, imageSize, cardHeight);
    ctx.strokeRect(x + padding/2, y + padding/2, imageSize, cardHeight);
    
    // Product image
    const imgSize = imageSize - (cardPadding * 2);
    const imgHeight = imgSize * 0.6; // 60% for image
    ctx.drawImage(img, x + padding/2 + cardPadding, y + padding/2 + cardPadding, imgSize, imgHeight);
    
    // Image border
    ctx.strokeStyle = '#2C3E50';
    ctx.lineWidth = 3;
    ctx.strokeRect(x + padding/2 + cardPadding, y + padding/2 + cardPadding, imgSize, imgHeight);
    
    // Text area
    const textY = y + padding/2 + cardPadding + imgHeight + 10;
    const textHeight = cardHeight - imgHeight - (cardPadding * 2) - 10;
    
    // Product title
    ctx.fillStyle = '#2C3E50';
    ctx.font = 'bold 28px Arial, sans-serif';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
    
    // Wrap text
    const title = item.title || 'Product';
    const maxWidth = imgSize - 10;
    const words = title.split(' ');
    let line = '';
    let lineHeight = 32;
    let currentY = textY + 5;
    
    for (let i = 0; i < words.length; i++) {
      const testLine = line + words[i] + ' ';
      const metrics = ctx.measureText(testLine);
      
      if (metrics.width > maxWidth && i > 0) {
        ctx.fillText(line, x + padding/2 + cardPadding + 5, currentY);
        line = words[i] + ' ';
        currentY += lineHeight;
        if (currentY > textY + textHeight - 40) break; // Leave space for price
      } else {
        line = testLine;
      }
    }
    if (line.length > 0 && currentY <= textY + textHeight - 40) {
      ctx.fillText(line, x + padding/2 + cardPadding + 5, currentY);
    }
    
    // Price badge - Neo-Brutalist
    if (item.price) {
      const priceY = textY + textHeight - 35;
      const priceWidth = 120;
      const priceHeight = 30;
      
      // Price background
      ctx.fillStyle = '#FF6B35';
      ctx.fillRect(x + padding/2 + cardPadding, priceY, priceWidth, priceHeight);
      
      // Price border
      ctx.strokeStyle = '#2C3E50';
      ctx.lineWidth = 3;
      ctx.strokeRect(x + padding/2 + cardPadding, priceY, priceWidth, priceHeight);
      
      // Price text
      ctx.fillStyle = '#FFFEF7';
      ctx.font = 'bold 16px Arial, sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(item.price, x + padding/2 + cardPadding + priceWidth/2, priceY + priceHeight/2);
    }
    
    // Add to cart icon/button
    const btnX = x + padding/2 + imageSize - cardPadding - 40;
    const btnY = textY + textHeight - 35;
    const btnSize = 30;
    
    // Button background
    ctx.fillStyle = '#4ECDC4';
    ctx.fillRect(btnX, btnY, btnSize, btnSize);
    
    // Button border  
    ctx.strokeStyle = '#2C3E50';
    ctx.lineWidth = 3;
    ctx.strokeRect(btnX, btnY, btnSize, btnSize);
    
    // Cart icon (simplified)
    ctx.fillStyle = '#2C3E50';
    ctx.font = 'bold 18px Arial, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('🛒', btnX + btnSize/2, btnY + btnSize/2);
  }

  initDiscInstances(count) {
    if (!this.gl || !this.discVAO) return;
    const gl = this.gl;

    const matricesArray = new Float32Array(count * 16);
    const matrices = [];
    for (let i = 0; i < count; ++i) {
      const instanceMatrixArray = new Float32Array(matricesArray.buffer, i * 16 * 4, 16);
      mat4.identity(instanceMatrixArray);
      matrices.push(instanceMatrixArray);
    }

    this.discInstances = {
      matricesArray,
      matrices,
      buffer: gl.createBuffer()
    };

    gl.bindVertexArray(this.discVAO);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.discInstances.buffer);
    gl.bufferData(gl.ARRAY_BUFFER, this.discInstances.matricesArray.byteLength, gl.DYNAMIC_DRAW);

    const mat4AttribSlotCount = 4;
    const bytesPerMatrix = 16 * 4;
    for (let j = 0; j < mat4AttribSlotCount; ++j) {
      const loc = this.discLocations.aInstanceMatrix + j;
      gl.enableVertexAttribArray(loc);
      gl.vertexAttribPointer(loc, 4, gl.FLOAT, false, bytesPerMatrix, j * 4 * 4);
      gl.vertexAttribDivisor(loc, 1);
    }
    gl.bindBuffer(gl.ARRAY_BUFFER, null);
    gl.bindVertexArray(null);
  }

  animate(deltaTime) {
    if (!this.gl) return;
    this.control.update(deltaTime, this.TARGET_FRAME_DURATION);

    const positions = this.instancePositions.map(p => vec3.transformQuat(vec3.create(), p, this.control.orientation));
    const scale = 0.25;
    const SCALE_INTENSITY = 0.6;

    positions.forEach((p, ndx) => {
      const s = (Math.abs(p[2]) / this.SPHERE_RADIUS) * SCALE_INTENSITY + (1 - SCALE_INTENSITY);
      const finalScale = s * scale;
      const matrix = mat4.create();

      // Create transformation matrix
      mat4.identity(matrix);
      
      // Move to origin
      const negP = [-p[0], -p[1], -p[2]];
      const translateToOrigin = mat4.create();
      mat4.identity(translateToOrigin);
      translateToOrigin[12] = negP[0];
      translateToOrigin[13] = negP[1];
      translateToOrigin[14] = negP[2];
      mat4.multiply(matrix, matrix, translateToOrigin);
      
      // Scale
      const scaleMatrix = mat4.create();
      mat4.identity(scaleMatrix);
      scaleMatrix[0] = finalScale;
      scaleMatrix[5] = finalScale;
      scaleMatrix[10] = finalScale;
      mat4.multiply(matrix, matrix, scaleMatrix);
      
      // Translate back
      const translateBack = mat4.create();
      mat4.identity(translateBack);
      translateBack[12] = 0;
      translateBack[13] = 0;
      translateBack[14] = -this.SPHERE_RADIUS;
      mat4.multiply(matrix, matrix, translateBack);

      // Copy to instances array
      for (let i = 0; i < 16; i++) {
        this.discInstances.matrices[ndx][i] = matrix[i];
      }
    });

    this.gl.bindBuffer(this.gl.ARRAY_BUFFER, this.discInstances.buffer);
    this.gl.bufferSubData(this.gl.ARRAY_BUFFER, 0, this.discInstances.matricesArray);
    this.gl.bindBuffer(this.gl.ARRAY_BUFFER, null);

    this.smoothRotationVelocity = this.control.rotationVelocity;
  }

  render() {
    if (!this.gl || !this.discProgram) return;
    const gl = this.gl;

    gl.useProgram(this.discProgram);
    gl.enable(gl.CULL_FACE);
    gl.enable(gl.DEPTH_TEST);

    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);

    gl.uniformMatrix4fv(this.discLocations.uWorldMatrix, false, this.worldMatrix);
    gl.uniformMatrix4fv(this.discLocations.uViewMatrix, false, this.camera.matrices.view);
    gl.uniformMatrix4fv(this.discLocations.uProjectionMatrix, false, this.camera.matrices.projection);
    gl.uniform3f(
      this.discLocations.uCameraPosition,
      this.camera.position[0],
      this.camera.position[1],
      this.camera.position[2]
    );
    gl.uniform4f(
      this.discLocations.uRotationAxisVelocity,
      this.control.rotationAxis[0],
      this.control.rotationAxis[1],
      this.control.rotationAxis[2],
      this.smoothRotationVelocity * 1.1
    );

    gl.uniform1i(this.discLocations.uItemCount, this.items.length);
    gl.uniform1i(this.discLocations.uAtlasSize, this.atlasSize);

    gl.uniform1i(this.discLocations.uTex, 0);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, this.tex);

    gl.bindVertexArray(this.discVAO);
    gl.drawElementsInstanced(
      gl.TRIANGLES,
      this.cardBuffers.indices.length,
      gl.UNSIGNED_SHORT,
      0,
      this.DISC_INSTANCE_COUNT
    );
    gl.bindVertexArray(null);
  }

  updateCameraMatrix() {
    // Simple lookAt implementation
    const eye = this.camera.position;
    const target = [0, 0, 0];
    const up = this.camera.up;
    
    const zAxis = vec3.create();
    zAxis[0] = eye[0] - target[0];
    zAxis[1] = eye[1] - target[1]; 
    zAxis[2] = eye[2] - target[2];
    vec3.normalize(zAxis, zAxis);
    
    const xAxis = vec3.cross(vec3.create(), up, zAxis);
    vec3.normalize(xAxis, xAxis);
    
    const yAxis = vec3.cross(vec3.create(), zAxis, xAxis);
    
    this.camera.matrices.view[0] = xAxis[0];
    this.camera.matrices.view[1] = yAxis[0];
    this.camera.matrices.view[2] = zAxis[0];
    this.camera.matrices.view[3] = 0;
    this.camera.matrices.view[4] = xAxis[1];
    this.camera.matrices.view[5] = yAxis[1];
    this.camera.matrices.view[6] = zAxis[1];
    this.camera.matrices.view[7] = 0;
    this.camera.matrices.view[8] = xAxis[2];
    this.camera.matrices.view[9] = yAxis[2];
    this.camera.matrices.view[10] = zAxis[2];
    this.camera.matrices.view[11] = 0;
    this.camera.matrices.view[12] = -vec3.dot(xAxis, eye);
    this.camera.matrices.view[13] = -vec3.dot(yAxis, eye);
    this.camera.matrices.view[14] = -vec3.dot(zAxis, eye);
    this.camera.matrices.view[15] = 1;
  }

  updateProjectionMatrix() {
    if (!this.gl) return;
    const canvasEl = this.gl.canvas;
    this.camera.aspect = canvasEl.clientWidth / canvasEl.clientHeight;
    const height = this.SPHERE_RADIUS * 0.35;
    const distance = this.camera.position[2];
    if (this.camera.aspect > 1) {
      this.camera.fov = 2 * Math.atan(height / distance);
    } else {
      this.camera.fov = 2 * Math.atan(height / this.camera.aspect / distance);
    }
    mat4.perspective(
      this.camera.matrices.projection,
      this.camera.fov,
      this.camera.aspect,
      this.camera.near,
      this.camera.far
    );
    mat4.invert(this.camera.matrices.inversProjection, this.camera.matrices.projection);
  }

  onControlUpdate(deltaTime) {
    const timeScale = deltaTime / this.TARGET_FRAME_DURATION + 0.0001;
    let damping = 5 / timeScale;
    let cameraTargetZ = 3;

    const isMoving = this.control.isPointerDown || Math.abs(this.smoothRotationVelocity) > 0.01;

    if (isMoving !== this.movementActive) {
      this.movementActive = isMoving;
      this.onMovementChange(isMoving);
    }

    if (!this.control.isPointerDown) {
      const nearestVertexIndex = this.findNearestVertexIndex();
      const itemIndex = nearestVertexIndex % Math.max(1, this.items.length);
      
      // Update current active index
      if (itemIndex !== this.currentActiveIndex) {
        this.currentActiveIndex = itemIndex;
        this.onActiveItemChange(itemIndex);
      }
      
      const snapDirection = vec3.normalize(vec3.create(), this.getVertexWorldPosition(nearestVertexIndex));
      this.control.snapTargetDirection = snapDirection;
    } else {
      cameraTargetZ += this.control.rotationVelocity * 80 + 2.5;
      damping = 7 / timeScale;
    }

    this.camera.position[2] += (cameraTargetZ - this.camera.position[2]) / damping;
    this.updateCameraMatrix();
  }

  findNearestVertexIndex() {
    const n = this.control.snapDirection;
    const inversOrientation = quat.create();
    // Conjugate quaternion for inverse
    inversOrientation[0] = -this.control.orientation[0];
    inversOrientation[1] = -this.control.orientation[1];
    inversOrientation[2] = -this.control.orientation[2];
    inversOrientation[3] = this.control.orientation[3];
    
    const nt = vec3.transformQuat(vec3.create(), n, inversOrientation);

    let maxD = -1;
    let nearestVertexIndex = 0;
    for (let i = 0; i < this.instancePositions.length; ++i) {
      const d = vec3.dot(nt, this.instancePositions[i]);
      if (d > maxD) {
        maxD = d;
        nearestVertexIndex = i;
      }
    }
    return nearestVertexIndex;
  }

  getVertexWorldPosition(index) {
    const nearestVertexPos = this.instancePositions[index];
    return vec3.transformQuat(vec3.create(), nearestVertexPos, this.control.orientation);
  }
}

// Export for Shopify integration
window.InfiniteGridMenu = InfiniteGridMenu;
